import { each, isNil, isNumber, sortBy } from 'lodash'
import { DataItem } from 'scrivito'

import { BoardSections } from './types'
import { SchemaAttributes } from '@/Data/types'
import { ensureString } from '@/utils/ensureString'
import { filterDataClassEnumValues } from '@/utils/filterDataClassEnumValues'
import { getDisplayValue } from '@/utils/getDisplayValue'
import { getLocalizedSchemaAttributes } from '@/utils/schemaLocalization'

export const getItemsByStatus = (items: DataItem[], status: string) => {
  return items.filter((item) => item.get('status') === status)
}

export const getItemById = (items: DataItem[], id: string) => {
  return items.find((item) => item.id() === id)
}

export const getStatusTitle = (status: string, schema: DataItem) => {
  return getDisplayValue({ rawValue: status, key: 'status', schema })
}

export const getStatus = (item: DataItem) => {
  return ensureString(item.get('status'))
}

export const setStatus = async (item: DataItem, status: string) => {
  if (item && status && item.get('status') !== status) {
    return item.update({ status: status })
  }
}

export const getInitialOrder = (
  items: DataItem[],
  initialItems: DataItem[],
) => {
  return sortBy(items, (item) =>
    initialItems.findIndex((initi) => initi.id() === item.id()),
  )
}

export const isStateAllowed = (
  item: DataItem,
  status: string,
  schema: DataItem,
) => {
  const schemaAttributes = getLocalizedSchemaAttributes(
    schema.get('attributes') as SchemaAttributes,
  )
  const { options } = filterDataClassEnumValues(
    (key: string) => item.get(key),
    schemaAttributes?.['status']?.enumValues,
    schemaAttributes,
  )
  const allowedStates = (options || []).map((e) => e.value)
  return allowedStates.includes(status)
}

export const initializeBoard = (items: DataItem[], schema: DataItem) => {
  const boardSections: BoardSections = {}
  const schemaAttributes = getLocalizedSchemaAttributes(
    schema.get('attributes') as SchemaAttributes,
  )

  const boardSectionKeys =
    schemaAttributes?.['status']?.enumValues?.map((e) => e.value) || []

  boardSectionKeys.forEach((boardSectionKey) => {
    boardSections[boardSectionKey] = getItemsByStatus(items, boardSectionKey)
  })

  return boardSections
}

export const findBoardSectionContainer = (
  boardSections: BoardSections,
  id: string,
) => {
  if (id in boardSections) {
    return id
  }

  const container = Object.keys(boardSections).find((key) =>
    boardSections[key]?.find((item) => item.id() === id),
  )
  return container
}

export const getSectionProbability = (key: string, schema: DataItem) => {
  const schemaAttributes = getLocalizedSchemaAttributes(
    schema.get('attributes') as SchemaAttributes,
  )
  const probability = schemaAttributes?.['status']?.enumValues?.find(
    (e) => e.value === key,
  )?.data?.probability
  return !isNil(probability) ? probability : 100
}

export const getSectionPrice = (
  section: DataItem[],
  schema: DataItem,
  probability: number = 100,
) => {
  const sumsPerCurrency: { [key: string]: number } = {}
  each(section, (item) => {
    const currency = ensureString(item.get('totalPriceCurrency'))
    const price = item.get('totalPrice')
    if (isNumber(price)) {
      if (isNumber(sumsPerCurrency[currency])) {
        sumsPerCurrency[currency] += price
      } else {
        sumsPerCurrency[currency] = price
      }
    }
  })

  const formattedPerCurrency: string[] = []
  each(Object.keys(sumsPerCurrency), (currency) => {
    formattedPerCurrency.push(
      `${getDisplayValue({
        rawValue: ((sumsPerCurrency[currency] || 0) * probability) / 100,
        key: 'totalPrice',
        schema,
      })} ${getDisplayValue({
        rawValue: currency,
        key: 'totalPriceCurrency',
        schema,
      })}`,
    )
  })

  return formattedPerCurrency.join(', ')
}
